import React from "react";

import { isEmpty } from "lodash-es";
import { useForm } from "react-hook-form";

import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { ServiceAccountOAuthClientInfo } from "src/clients/taktile-api";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useUpdateOAuthClient } from "src/oauthClients/api/queries";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type Inputs = {
  name: string;
};

type Props = {
  client?: ServiceAccountOAuthClientInfo;
  open: boolean;
  organizationId: string;
  workspaceId: string;
  onClose: () => void;
  afterLeave: () => void;
};

export const RenameOAuthClientModal: React.FC<Props> = ({
  client,
  open,
  organizationId: orgId,
  workspaceId: wsId,
  onClose,
  afterLeave,
}) => {
  const updateOAuthClient = useUpdateOAuthClient(
    orgId,
    wsId,
    client?.client_id ?? "",
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<Inputs>({
    mode: "onChange",
    values: client ? { name: client.client_name } : undefined,
  });

  const onConfirm = handleSubmit(async (input: Inputs) => {
    if (!client) return;

    try {
      await updateOAuthClient.mutateAsync({ name: input.name });
    } catch (error) {
      logger.error(`Error when renaming OAuth client: ${error}`);
      toastActions.failure({
        title: "Failed to rename the OAuth client",
        description: errorMessage(error),
      });
      return;
    }
    onClose();
  });

  return (
    <Modal afterLeave={afterLeave} open={open} onClose={onClose}>
      <Modal.Header>Rename OAuth client</Modal.Header>
      <form onSubmit={onConfirm}>
        <Modal.Content>
          <Input
            {...register("name", {
              required: true,
              validate: validateNotStartsWithInternalPrefix(
                `OAuth client name cannot start with ${taktileInternalPrefix}`,
              ),
            })}
            error={Boolean(errors.name?.message)}
            placeholder="Name your OAuth client"
          />
          {Boolean(errors.name?.message) && (
            <ErrorHint>{errors.name?.message}</ErrorHint>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="oauth-client-rename-submit"
              isDisabled={
                !isEmpty(errors) || !isValid || isSubmitting || !isDirty
              }
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Save name
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
